import React from 'react';
import { MessageCircle } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger } from "@/components/ui/sheet";
import CommentsList from './comments/CommentsList';
import CommentsInput from './comments/CommentsInput';
import { useToast } from "@/hooks/use-toast";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/contexts/AuthContext";
import { useComments } from "@/hooks/use-comments";
import { useProfile } from "@/hooks/use-profile";

interface CommentsProps {
  videoId: string;
  isMobile?: boolean;
}

const Comments: React.FC<CommentsProps> = ({ videoId, isMobile = false }) => {
  const [newComment, setNewComment] = React.useState('');
  const [isLoading, setIsLoading] = React.useState(false);
  const { toast } = useToast();
  const { user } = useAuth();
  const { comments } = useComments(videoId);
  const { profile } = useProfile(user?.id);

  const handleCommentChange = React.useCallback((e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setNewComment(e.target.value);
  }, []);

  const handleCommentSubmit = async () => {
    if (!user) {
      toast({
        title: "Not logged in",
        description: "Please log in to post comments",
        variant: "destructive"
      });
      return;
    }

    if (!newComment.trim()) {
      toast({
        title: "Empty comment",
        description: "Please write something before posting",
        variant: "destructive"
      });
      return;
    }

    try {
      setIsLoading(true);
      const { error } = await supabase
        .from('comments')
        .insert({
          text: newComment.trim(),
          user_id: user.id,
          video_id: videoId
        });

      if (error) throw error;

      setNewComment('');
      toast({
        title: "Comment posted",
        description: "Your comment has been posted successfully"
      });
    } catch (error) {
      console.error('Error posting comment:', error);
      toast({
        title: "Error",
        description: "Failed to post comment. Please try again.",
        variant: "destructive"
      });
    } finally {
      setIsLoading(false);
    }
  };

  const commentsContent = (
    <div className="mt-6">
      <CommentsInput 
        value={newComment} 
        onChange={handleCommentChange} 
        onSubmit={handleCommentSubmit}
        isLoading={isLoading}
        userAvatar={profile?.profile_image_url}
        userName={profile?.display_name || profile?.username}
      />
      <CommentsList comments={comments} />
    </div>
  );

  if (isMobile) {
    return (
      <Sheet>
        <SheetTrigger asChild>
          <Button variant="ghost" size="sm" className="text-white">
            <MessageCircle className="w-5 h-5 mr-2" />
            {comments.length} Comments
          </Button>
        </SheetTrigger>
        <SheetContent side="bottom" className="h-[80vh] bg-black/95 border-t border-white/10">
          <SheetHeader>
            <SheetTitle className="text-white">Comments</SheetTitle>
          </SheetHeader>
          {commentsContent}
        </SheetContent>
      </Sheet>
    );
  }

  return (
    <div className="space-y-6">
      {commentsContent}
    </div>
  );
};

export default Comments;
